/**
 * Les papiers des équipes.
 *
 * Le patron, le 13 septembre : « il faut que le patron sache en avance quand
 * dire à ses gars qu'il faut renouveler et s'inscrire ». Tout ce fichier tourne
 * autour de ces deux mots — **en avance**, et **s'inscrire**. Un papier n'est pas
 * urgent le jour où il expire : il l'est le jour où il faut réserver la session
 * pour ne pas se retrouver avec un cariste qui ne peut plus conduire.
 *
 * Tout ici est pur.
 */
import { daysUntil } from "../deadlines/types.js";

export type CertificationKind =
  | "caces"
  | "visite_medicale"
  | "sst"
  | "aipr"
  | "habilitation_electrique"
  | "travail_hauteur"
  | "echafaudage"
  | "amiante"
  | "permis"
  | "autre";

export interface Certification {
  id: string;
  employeeId: string;
  kind: CertificationKind;
  /** La précision qui fait le papier : « R482 cat. B1 », « Permis C ». */
  label: string | null;
  obtainedOn: string | null;
  /** Nulle pour un papier sans échéance. */
  expiresOn: string | null;
  /** La session ou la visite est réservée. */
  renewalPlannedOn: string | null;
  notes: string | null;
}

interface KindRule {
  label: string;
  /**
   * Combien de jours avant l'expiration il faut **agir** — prévenir le
   * compagnon et réserver. Ce n'est pas la date d'expiration moins un
   * arrondi : c'est le délai réel pour obtenir une place.
   */
  leadDays: number;
  /**
   * La durée de validité usuelle, en mois, pour **proposer** une date
   * d'expiration à la saisie. `null` quand elle varie trop pour être proposée
   * honnêtement — la visite médicale dépend du suivi (2, 4 ou 5 ans), un
   * permis n'expire pas de la même façon selon la catégorie. Proposée, jamais
   * imposée : la date qui fait foi est celle du document.
   */
  usualValidityMonths: number | null;
  /** Ce que l'écran rappelle sous le champ. */
  hint: string;
}

export const CERTIFICATION_RULES: Record<CertificationKind, KindRule> = {
  caces: {
    label: "CACES",
    leadDays: 60,
    usualValidityMonths: 60,
    hint: "5 ans en général, 10 ans pour la R482 (engins de chantier).",
  },
  visite_medicale: {
    label: "Visite médicale",
    leadDays: 60,
    usualValidityMonths: null,
    hint: "La date de la prochaine visite figure sur l'attestation de suivi.",
  },
  sst: {
    label: "SST",
    leadDays: 60,
    usualValidityMonths: 24,
    hint: "Recyclage (MAC SST) tous les 24 mois.",
  },
  aipr: {
    label: "AIPR",
    leadDays: 60,
    usualValidityMonths: 60,
    hint: "Valable 5 ans.",
  },
  habilitation_electrique: {
    label: "Habilitation électrique",
    leadDays: 60,
    usualValidityMonths: 36,
    hint: "Recyclage recommandé tous les 3 ans.",
  },
  travail_hauteur: {
    label: "Travail en hauteur",
    leadDays: 45,
    usualValidityMonths: null,
    hint: "Pas de durée légale : reportez celle fixée par l'organisme.",
  },
  echafaudage: {
    label: "Échafaudage",
    leadDays: 45,
    usualValidityMonths: null,
    hint: "Pas de durée légale : reportez celle fixée par l'organisme.",
  },
  amiante: {
    label: "Amiante (SS4)",
    leadDays: 60,
    usualValidityMonths: 36,
    hint: "Recyclage tous les 3 ans.",
  },
  permis: {
    label: "Permis",
    leadDays: 45,
    usualValidityMonths: null,
    hint: "Poids lourd : la date de fin figure sur le permis.",
  },
  autre: {
    label: "Autre",
    leadDays: 30,
    usualValidityMonths: null,
    hint: "",
  },
};

export const CERTIFICATION_KINDS = Object.keys(CERTIFICATION_RULES) as CertificationKind[];

/** « CACES R482 cat. B1 », ou « SST » quand il n'y a pas de précision. */
export function certificationName(c: Pick<Certification, "kind" | "label">): string {
  const base = CERTIFICATION_RULES[c.kind].label;
  const precision = c.label?.trim();
  return precision ? `${base} ${precision}` : base;
}

/**
 * L'état d'un papier, du plus pressant au plus tranquille.
 *
 *   · `expired`   — expiré, et rien de réservé ;
 *   · `to_book`   — dans le délai d'anticipation, rien de réservé : **c'est le
 *                   moment de prévenir le compagnon et de l'inscrire** ;
 *   · `booked`    — expiré ou proche, mais la session est réservée ;
 *   · `valid`     — rien à faire avant longtemps ;
 *   · `permanent` — pas d'échéance.
 *
 * Une réservation passée ne compte plus : une session prévue le 3 et qu'on n'a
 * pas remplacée par un nouveau papier le 10 n'a manifestement pas eu lieu, ou
 * n'a pas été reportée — dans les deux cas il faut s'en occuper.
 */
export type CertificationStatus = "expired" | "to_book" | "booked" | "valid" | "permanent";

export function certificationStatus(
  c: Pick<Certification, "kind" | "expiresOn" | "renewalPlannedOn">,
  today: string,
): CertificationStatus {
  if (!c.expiresOn) return "permanent";
  const jours = daysUntil(c.expiresOn, today);
  if (jours > CERTIFICATION_RULES[c.kind].leadDays) return "valid";
  if (c.renewalPlannedOn && c.renewalPlannedOn >= today) return "booked";
  return jours < 0 ? "expired" : "to_book";
}

const RANG: Record<CertificationStatus, number> = {
  expired: 0,
  to_book: 1,
  booked: 2,
  valid: 3,
  permanent: 4,
};

/** Ce qui demande l'attention : expiré, à réserver, ou réservé. */
export function needsAttention(status: CertificationStatus): boolean {
  return status === "expired" || status === "to_book" || status === "booked";
}

export interface CertificationDue {
  certification: Certification;
  employeeName: string;
  status: CertificationStatus;
  /** Jours avant expiration ; négatif une fois expiré. */
  daysLeft: number;
}

/**
 * Les papiers à traiter, pour toute l'équipe, le plus pressant d'abord.
 *
 * Les salariés archivés sont écartés : un papier qui expire chez quelqu'un qui
 * a quitté l'entreprise ne demande aucun geste, et l'afficher ferait douter du
 * reste de la liste.
 */
export function certificationsDue(
  certifications: readonly Certification[],
  employees: readonly { id: string; name: string; isActive: boolean }[],
  today: string,
): CertificationDue[] {
  const actifs = new Map(employees.filter((e) => e.isActive).map((e) => [e.id, e.name]));
  return certifications
    .flatMap<CertificationDue>((c) => {
      const nom = actifs.get(c.employeeId);
      if (nom == null || !c.expiresOn) return [];
      const status = certificationStatus(c, today);
      if (!needsAttention(status)) return [];
      return [{ certification: c, employeeName: nom, status, daysLeft: daysUntil(c.expiresOn, today) }];
    })
    .sort((a, b) => RANG[a.status] - RANG[b.status] || a.daysLeft - b.daysLeft);
}

/**
 * La date d'expiration à proposer, depuis la date d'obtention.
 *
 * `null` quand le type n'a pas de durée usuelle fiable, ou sans date
 * d'obtention. Le calcul est au mois calendaire : un SST passé le 31 août
 * expire fin août deux ans plus tard, pas le 1er septembre.
 */
export function suggestedExpiry(kind: CertificationKind, obtainedOn: string | null): string | null {
  const mois = CERTIFICATION_RULES[kind].usualValidityMonths;
  if (mois == null || !obtainedOn) return null;
  const [a, m, j] = obtainedOn.slice(0, 10).split("-").map(Number) as [number, number, number];
  const total = m - 1 + mois;
  const annee = a + Math.floor(total / 12);
  const moisCible = (total % 12) + 1;
  const dernierJour = new Date(Date.UTC(annee, moisCible, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${annee}-${pad(moisCible)}-${pad(Math.min(j, dernierJour))}`;
}
